import { useApolloClient, useQuery } from "@apollo/client";
import { faCheckCircle, faEdit } from "@fortawesome/free-solid-svg-icons";
import { BulmaButton, CancelButton, SubmitButton } from "Components/Button";
import { Horizontal } from "Components/Horizontal";
import { ButtonIcon } from "Components/Icon";
import { LcpUserPageWrapper } from "Components/MainPageWrapper";
import { Modal } from "Components/Modal";
import { useModalState } from "Components/ModalState";
import { Section } from "Components/Section";
import * as DateFns from "date-fns";
import {
  AdminApprovalQuoteQuery,
  adminApprovalQuoteQuery,
  submitAdminApprovalMutation,
} from "generated/graphql";
import { Checkbox } from "Inputs/Checkbox";
import { SimpleFieldProps, useFormWithSchema } from "Inputs/FormTools";
import { PercentInput } from "Inputs/PercentInput";
import { Textarea } from "Inputs/Textarea";
import { YupDefined } from "Inputs/Tools";
import React, { useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import {
  formatDollarsAndCents,
  formatPercent,
  formatWholeDollars,
  hasValueFn,
  hasValue,
  hv,
} from "Tools";
import * as Yup from "yup";
import { WhatIsThisModal } from "../Components/WhatIsThisModal";
import { AdminAttachmentControl } from "./AdminAttachmentControl";
import { formatAppType } from "./ApplicationDetails/ApplicationDetailsPage";
import { ApplicationNotFoundPage } from "./ApplicationNotFoundPage";
import { AdminPaths } from "./Paths";

const minBasePremiumRate = 0.07;

export function ApproveOrDenyPurchasePage() {
  const { adminApprovalKey } = useParams<{ adminApprovalKey: string }>();
  const form = useComponentFormik({ adminApprovalKey });
  const basePremiumRateOverrideField = form.field.getSubField(
    "basePremiumRateOverride",
  );
  const hasAdminApprovalField = form.field.hasAdminApproval;
  const [basePremiumRateOverrideForQuery, setBasePremiumRateOverrideForQuery] =
    useState(basePremiumRateOverrideField.value);
  const variables = {
    adminApprovalKey,
    basePremiumRateOverride: basePremiumRateOverrideForQuery,
  };
  const confirmModalState = useModalState();

  const { data, refetch } = useQuery(adminApprovalQuoteQuery, { variables });
  if (data) {
    if (!hv(data.adminApprovalQuote)) {
      return <ApplicationNotFoundPage />;
    }
    if (!data.adminApprovalQuote.needsAdminApproval) {
      return (
        <NotAvailableForReviewPage
          appId={data.adminApprovalQuote.applicationId}
        />
      );
    }
  }

  return (
    <LcpUserPageWrapper documentTitle="Approve or Deny Policy Purchase">
      <Section title="Approve or Deny Policy Purchase">
        {!data ? (
          "Loading..."
        ) : (
          <QuoteInfoSection quote={data.adminApprovalQuote!} />
        )}
        <ModifyBasePremiumRateControlWithModal
          fieldProps={basePremiumRateOverrideField}
          onSubmit={setBasePremiumRateOverrideForQuery}
        />
        <NoteInput fieldProps={form.field.getSubField("note")} />
        {data?.adminApprovalQuote && (
          <AdminAttachmentControl
            appId={data.adminApprovalQuote.applicationId}
            attachments={data?.adminAttachments}
            refetch={() => refetch(variables)}
          />
        )}
        <div className="buttons is-centered" style={{ marginTop: 20 }}>
          <CancelButton
            onClick={async () => {
              await hasAdminApprovalField.onChange(false);
              confirmModalState.open();
            }}
            isSubmitting={!hasAdminApprovalField.value && form.isSubmitting}
            disabled={form.isSubmitting}
          >
            Deny Purchase
          </CancelButton>
          <SubmitButton
            onClick={async () => {
              await hasAdminApprovalField.onChange(true);
              confirmModalState.open();
            }}
            isSubmitting={hasAdminApprovalField.value && form.isSubmitting}
            disabled={form.isSubmitting}
          >
            <ButtonIcon icon={faCheckCircle} />
            <span>Approve Purchase</span>
          </SubmitButton>
        </div>
        {hv(data?.adminApprovalQuote) && (
          <div className="has-text-centered">
            <Link
              to={AdminPaths.ApplicationDetails({
                app: { id: data?.adminApprovalQuote?.applicationId ?? "" },
              })}
            >
              Back to Application Details
            </Link>
          </div>
        )}
      </Section>
      <Modal
        {...confirmModalState}
        title={
          <>
            {hasAdminApprovalField.value ? "Approve" : "Deny"} Policy Purchase
          </>
        }
        buttonRow={
          <>
            <CancelButton
              onClick={() => confirmModalState.close()}
              disabled={form.isSubmitting}
            >
              Cancel
            </CancelButton>
            <SubmitButton
              onClick={async () => {
                await form.submitForm();
                confirmModalState.close();
              }}
              isSubmitting={form.isSubmitting}
              disabled={form.isSubmitting}
            >
              OK
            </SubmitButton>
          </>
        }
      >
        <div>
          {hasAdminApprovalField.value ? "Approve" : "Deny"} policy purchase for
          applicant {data?.adminApprovalQuote?.companyName}?
        </div>
        {!hasAdminApprovalField.value && (
          <div style={{ marginTop: "1em" }}>
            <Checkbox field={form.field.shouldSendDenyEmail}>
              Send automated declination email to{" "}
              {data?.adminApprovalQuote?.applicantEmail}:{" "}
              <em>
                "Due to the application answers entered, we are unable to
                provide a quote for coverage at this time."
              </em>
            </Checkbox>
          </div>
        )}
      </Modal>
    </LcpUserPageWrapper>
  );
}

function useComponentFormik(props: { adminApprovalKey: string }) {
  const history = useHistory();
  const apolloClient = useApolloClient();

  return useFormWithSchema({
    initialValues: {
      basePremiumRateOverride: null,
      hasAdminApproval: false,
      note: "",
      shouldSendDenyEmail: false,
    },
    onSubmit: async values => {
      const result = await apolloClient.mutate({
        mutation: submitAdminApprovalMutation,
        variables: {
          input: {
            adminApprovalKey: props.adminApprovalKey,
            basePremiumRateOverride: values.basePremiumRateOverride,
            hasAdminApproval: values.hasAdminApproval,
            note: values.note,
            shouldSendDenyEmail: values.shouldSendDenyEmail,
          },
        },
      });
      if (!result.errors) {
        if (values.hasAdminApproval) {
          history.push(
            AdminPaths.PurchaseApproved({
              adminApprovalKey: props.adminApprovalKey,
            }),
          );
        } else {
          history.push(
            AdminPaths.PurchaseDenied({
              adminApprovalKey: props.adminApprovalKey,
            }),
          );
        }
      }
    },
    validationSchema: Yup.object({
      basePremiumRateOverride: PercentInput.getDefaultValidator()
        .min(
          minBasePremiumRate,
          ({ min }) =>
            `Please enter a premium rate of ${formatPercent(min)} or more.`,
        )
        .max(
          1,
          ({ max }) =>
            `Please enter a premium rate of ${formatPercent(max)} or less.`,
        ),
      hasAdminApproval: YupDefined.boolean(),
      note: YupDefined.string().nullable(),
      shouldSendDenyEmail: YupDefined.boolean(),
    }),
  });
}

export function PurchaseApprovedPage() {
  const { adminApprovalKey } = useParams<{ adminApprovalKey: string }>();
  const { data } = useQuery(adminApprovalQuoteQuery, {
    variables: { adminApprovalKey },
  });
  if (data && !data.adminApprovalQuote) {
    return <ApplicationNotFoundPage />;
  }
  return (
    <LcpUserPageWrapper documentTitle="Policy Purchase Approved">
      <Section title="Policy Purchase Approved">
        {!data ? (
          "Loading..."
        ) : (
          <>
            <div style={{ marginBottom: "2em" }}>
              The application for case{" "}
              <em>{data.adminApprovalQuote!.caseName}</em> has been approved for
              purchase and applicant{" "}
              <em>{data.adminApprovalQuote!.applicantName}</em> has been
              notified via email.
            </div>
            <div className="has-text-centered">
              <Link to={AdminPaths.AdminDashboard}>Return to Dashboard</Link>
            </div>
          </>
        )}
      </Section>
    </LcpUserPageWrapper>
  );
}

export function PurchaseDeniedPage() {
  const { adminApprovalKey } = useParams<{ adminApprovalKey: string }>();
  const { data } = useQuery(adminApprovalQuoteQuery, {
    variables: { adminApprovalKey },
  });
  if (data && !data.adminApprovalQuote) {
    return <ApplicationNotFoundPage />;
  }
  return (
    <LcpUserPageWrapper documentTitle="Policy Purchase Denied">
      <Section title="Policy Purchase Denied">
        {!data ? (
          "Loading..."
        ) : (
          <>
            <div style={{ marginBottom: "2em" }}>
              The application for case{" "}
              <em>{data.adminApprovalQuote!.caseName}</em> has been declined and
              applicant <em>{data.adminApprovalQuote!.applicantName}</em> has
              been notified via email.
            </div>
            <div className="has-text-centered">
              <Link to={AdminPaths.AdminDashboard}>Return to Dashboard</Link>
            </div>
          </>
        )}
      </Section>
    </LcpUserPageWrapper>
  );
}

export function ModifyBasePremiumRateControlWithModal(props: {
  fieldProps: SimpleFieldProps<number | null>;
  onSubmit: (basePremiumOverride: number | null) => void;
}) {
  const [isVisible, setVisible] = useState(false);
  const tryCloseModal = () => {
    if (props.fieldProps.error === undefined) {
      props.onSubmit(props.fieldProps.value);
      setVisible(false);
    }
  };
  return (
    <div className="buttons is-centered">
      <BulmaButton
        className="is-primary"
        onClick={async () => {
          setVisible(true);
          if (props.fieldProps.value === null) {
            await props.fieldProps.onChange(minBasePremiumRate);
          }
        }}
      >
        <ButtonIcon icon={faEdit} />
        <span>Modify Base Premium Rate</span>
      </BulmaButton>
      <Modal
        title="Modify Base Premium Rate"
        isVisible={isVisible}
        onCancel={tryCloseModal}
        buttonRow={<SubmitButton onClick={tryCloseModal}>OK</SubmitButton>}
      >
        <Horizontal label="Base Premium Rate">
          <PercentInput fieldProps={props.fieldProps} decimalScale={2} />
        </Horizontal>
      </Modal>
    </div>
  );
}

function EmptyRow() {
  return (
    <tr>
      <td></td>
      <td></td>
    </tr>
  );
}

export function QuoteInfoSection({
  quote,
}: {
  quote: NonNullable<AdminApprovalQuoteQuery["adminApprovalQuote"]>;
}) {
  return (
    <table
      className="table react-quote-table"
      style={{ fontSize: "0.9em", width: "100%" }}
    >
      <tbody>
        <tr>
          <td>Company Name</td>
          <td>{quote.companyName}</td>
        </tr>
        <tr>
          <td>Company State</td>
          <td>{quote.coverageState}</td>
        </tr>
        <tr>
          <td>Case Name</td>
          <td>{quote.caseName}</td>
        </tr>
        <tr>
          <td>Policy Type</td>
          <td>{formatAppType(quote.appType)}</td>
        </tr>
        <tr>
          <td>Case Jurisdiction</td>
          <td>{quote.jurisdiction}</td>
        </tr>
        {quote.jurisdiction.toUpperCase() === "FEDERAL" && (
          <tr>
            <td>Federal Court State</td>
            <td>{quote.federalCourtState}</td>
          </tr>
        )}
        <tr>
          <td>Policy Limit</td>
          <td>{formatWholeDollars(quote.policyLimit)}</td>
        </tr>
        <tr>
          <td>Fee Coverage</td>
          <td>
            {hasValueFn(quote.feeCoverageLimit, formatWholeDollars) ?? "No"}
          </td>
        </tr>
        <tr>
          <td>Date Of Service</td>
          <td>
            {hasValueFn(quote.dateOfService, dateStr =>
              DateFns.format(dateStr, "MMMM d, yyyy"),
            ) ?? "Not Yet Served"}
          </td>
        </tr>
        <EmptyRow />
        <EmptyRow />
        <tr>
          <td>Base Premium</td>
          <td>{formatDollarsAndCents(quote.basePremium)}</td>
        </tr>
        <tr>
          <td>Base Premium Rate</td>
          <td>{formatPercent(quote.basePremiumRate)}</td>
        </tr>
        {hasValue(quote.feeCoveragePremium) && (
          <tr>
            <td>Fee Coverage Premium</td>
            <td>{formatDollarsAndCents(quote.feeCoveragePremium)}</td>
          </tr>
        )}
        <tr>
          <td>Total Premium</td>
          <td>{formatDollarsAndCents(quote.premium)}</td>
        </tr>
        {quote.policyFee !== 0 && quote.policyFeeTax !== 0 && (
          <tr>
            <td>Policy Fee</td>
            <td>{formatDollarsAndCents(quote.policyFee)}</td>
          </tr>
        )}
        {quote.surplusLinesTax !== 0 && (
          <tr>
            <td>Surplus Lines Tax</td>
            <td>{formatDollarsAndCents(quote.surplusLinesTax)}</td>
          </tr>
        )}
        {hasValue(quote.stampingFee) && (
          <tr>
            <td>{quote.stampingFeeLabel}</td>
            <td>{formatDollarsAndCents(quote.stampingFee)}</td>
          </tr>
        )}
        {hasValue(quote.stateSpecificTaxOrFee) && (
          <tr>
            <td>{quote.stateSpecificTaxOrFeeName}</td>
            <td>{formatDollarsAndCents(quote.stateSpecificTaxOrFee)}</td>
          </tr>
        )}
        {quote.policyFee !== 0 && quote.policyFeeTax === 0 && (
          <tr>
            <td>Policy Fee</td>
            <td>{formatDollarsAndCents(quote.policyFee)}</td>
          </tr>
        )}
        <tr>
          <td>Total Payment</td>
          <td>{formatDollarsAndCents(quote.totalPaymentAmount)}</td>
        </tr>
      </tbody>
    </table>
  );
}

function NoteInput(props: { fieldProps: SimpleFieldProps<string | null> }) {
  return (
    <Horizontal label={<NoteLabel />}>
      <div className="field">
        <Textarea field={props.fieldProps} />
      </div>
    </Horizontal>
  );
}

function NoteLabel() {
  return (
    <>
      <span>Notes</span>{" "}
      <WhatIsThisModal title="Admin Review Notes">
        These notes are for <strong>internal use only</strong>. They will{" "}
        <strong>not</strong> be sent to the insured with their approval or
        denial notice. They <strong>are</strong> included in all reporting,
        including the Aspen bordereau.
      </WhatIsThisModal>
    </>
  );
}

export function NotAvailableForReviewPage({
  appId,
}: {
  appId: string | number;
}) {
  return (
    <LcpUserPageWrapper documentTitle="Approve or Deny Policy Purchase">
      <Section title="Approve or Deny Policy Purchase">
        <p>This application is no longer available for admin review.</p>
        <p>
          <a href={AdminPaths.ApplicationDetails({ app: { id: appId } })}>
            Click here to view Application Details
          </a>
          .
        </p>
        <div className="has-text-centered" style={{ marginTop: "1em" }}>
          <Link className="button is-primary" to={AdminPaths.AdminDashboard}>
            Return to Dashboard
          </Link>
        </div>
      </Section>
    </LcpUserPageWrapper>
  );
}
